import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const BROWN = '#6b3410';
const ConfirmContext = createContext(null);

/**
 * useConfirm() returns an async function. Drop-in replacement for window.confirm():
 *   const confirm = useConfirm();
 *   if (!await confirm({ title: 'Delete?', message: 'Cannot be undone.' })) return;
 *
 * Options:
 *   - title: string (required)
 *   - message: string (optional, supports \n)
 *   - confirmText: string (default 'Confirm')
 *   - cancelText: string (default 'Cancel')
 *   - tone: 'default' | 'danger'  (danger = red confirm button)
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmDialogProvider>');
  return ctx;
}

export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        title:       opts?.title || 'Are you sure?',
        message:     opts?.message || '',
        confirmText: opts?.confirmText || 'Confirm',
        cancelText:  opts?.cancelText  || 'Cancel',
        tone:        opts?.tone || 'default',
      });
    });
  }, []);

  const close = useCallback((value) => {
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
    setState(null);
  }, []);

  // Esc closes (cancels)
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(false); }
      if (e.key === 'Enter')  { e.preventDefault(); close(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif",
          }}
          onClick={(e) => e.target === e.currentTarget && close(false)}
        >
          <div style={{
            background: 'white', borderRadius: '14px', maxWidth: '420px', width: '100%',
            padding: '24px 26px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            border: '0.5px solid #e5e2db',
          }}>
            <div style={{
              fontSize: '16px', fontWeight: '600', color: '#1a1a1a',
              marginBottom: state.message ? '8px' : '20px',
              letterSpacing: '-0.2px',
            }}>{state.title}</div>
            {state.message && (
              <div style={{
                fontSize: '13px', color: '#666', marginBottom: '20px',
                lineHeight: 1.5, whiteSpace: 'pre-wrap',
              }}>{state.message}</div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => close(false)}
                style={{
                  background: 'transparent', border: '0.5px solid #d4d0c8',
                  padding: '9px 16px', borderRadius: '8px',
                  fontSize: '13px', color: '#555', cursor: 'pointer',
                  fontFamily: 'inherit', fontWeight: '500',
                }}
              >{state.cancelText}</button>
              <button
                onClick={() => close(true)}
                autoFocus
                style={{
                  background: state.tone === 'danger' ? '#c44' : BROWN,
                  border: 'none',
                  padding: '9px 18px', borderRadius: '8px',
                  color: 'white', fontSize: '13px', fontWeight: '600',
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >{state.confirmText}</button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
